import { getJSON } from '../../utils/request'

// Action Types
export const SET_FIND_TICKET_URL = 'ticketsScanner/ticket/SET_FIND_TICKET_URL'
export const SET_CHECK_IN_URL = 'ticketsScanner/ticket/SET_CHECK_IN_URL'
export const SET_REMOVE_CHECK_IN_URL = 'ticketsScanner/ticket/SET_REMOVE_CHECK_IN_URL'
export const FIND_TICKET_REQUEST = 'ticketsScanner/ticket/FIND_TICKET_REQUEST'
export const FIND_TICKET_SUCCESS = 'ticketsScanner/ticket/FIND_TICKET_SUCCESS'
export const FIND_TICKET_FAILURE = 'ticketsScanner/ticket/FIND_TICKET_FAILURE'
export const CHECK_IN_REQUEST = 'ticketsScanner/ticket/CHECK_IN_REQUEST'
export const CHECK_IN_SUCCESS = 'ticketsScanner/ticket/CHECK_IN_SUCCESS'
export const CHECK_IN_FAILURE = 'ticketsScanner/ticket/CHECK_IN_FAILURE'
export const REMOVE_CHECK_IN_REQUEST = 'ticketsScanner/ticket/REMOVE_CHECK_IN_REQUEST'
export const REMOVE_CHECK_IN_SUCCESS = 'ticketsScanner/ticket/REMOVE_CHECK_IN_SUCCESS'
export const REMOVE_CHECK_IN_FAILURE = 'ticketsScanner/ticket/REMOVE_CHECK_IN_FAILURE'
export const RESET_TICKET = 'ticketsScanner/ticket/RESET_TICKET'

// Initial State
const initialState = {
  ticket: null,
  findTicketUrl: null,
  checkInUrl: null,
  removeCheckInUrl: null,
  loading: false,
  error: null,
}

// Reducer
export default function ticketReducer(state = initialState, action) {
  switch (action.type) {
    case SET_FIND_TICKET_URL:
      return {
        ...state,
        findTicketUrl: action.payload,
      }

    case SET_CHECK_IN_URL:
      return {
        ...state,
        checkInUrl: action.payload,
      }

    case SET_REMOVE_CHECK_IN_URL:
      return {
        ...state,
        removeCheckInUrl: action.payload,
      }

    case FIND_TICKET_REQUEST:
    case CHECK_IN_REQUEST:
    case REMOVE_CHECK_IN_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      }

    case FIND_TICKET_SUCCESS:
      return {
        ...state,
        loading: false,
        ticket: action.payload,
      }

    case FIND_TICKET_FAILURE:
    case CHECK_IN_FAILURE:
    case REMOVE_CHECK_IN_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      }

    case CHECK_IN_SUCCESS:
    case REMOVE_CHECK_IN_SUCCESS:
      return {
        ...state,
        loading: false,
        ticket: null,
      }

    case RESET_TICKET:
      return {
        ...state,
        ticket: null,
        loading: false,
        error: null,
      }

    default:
      return state
  }
}

// Action Creators
export const setFindTicketUrl = (url) => ({
  type: SET_FIND_TICKET_URL,
  payload: url,
})

export const setCheckInUrl = (url) => ({
  type: SET_CHECK_IN_URL,
  payload: url,
})

export const setRemoveCheckInUrl = (url) => ({
  type: SET_REMOVE_CHECK_IN_URL,
  payload: url,
})

export const resetTicket = () => ({
  type: RESET_TICKET,
})

// Ticket barcodes are nine digits; require an exact match before lookup.
const isBarcodeString = (query) => /^\d{9}$/.test(query)

// Thunks
export const findTicket = (barcode) => (dispatch, getState) => {
  const state = getState()
  const { findTicketUrl } = state.ticketsScanner.ticket

  dispatch({ type: FIND_TICKET_REQUEST })

  // 404 is an expected control path for unknown barcodes. This call already opted out of the
  // global ajaxError flash with `global: false`, so nothing user-visible changes here.
  getJSON(findTicketUrl, { barcode })
    .then((ticket) => {
      dispatch({
        type: FIND_TICKET_SUCCESS,
        payload: ticket,
      })
    })
    .catch((error) => {
      if (error.status === 404) {
        dispatch({
          type: FIND_TICKET_SUCCESS,
          payload: { notFound: true },
        })
      } else {
        dispatch({
          type: FIND_TICKET_FAILURE,
          payload: error.message,
        })
      }
    })
}

// The two calls below stay on $.ajax on purpose. `tickets#check_in` and `tickets#remove_checkin`
// answer with a server-rendered `.js` template (`$('#flash').replaceWith(...)`, and for
// remove_checkin also `$('.modal').modal('hide')`) which jQuery executes as the response. fetch
// would not run it, so the flash would stop appearing and the modal would stay open. They also
// rely on the CSRF token that rails-ujs's jQuery prefilter adds to every non-GET $.ajax call.
// Both belong with the separate "*.js.erb responses -> JSON or Turbo Streams" cleanup.
export const checkInTicket = () => (dispatch, getState) => {
  const state = getState()
  const { ticket, checkInUrl } = state.ticketsScanner.ticket

  if (!ticket || !ticket.id) return

  const url = checkInUrl.replace(/:id/, ticket.id)

  dispatch({ type: CHECK_IN_REQUEST })

  window.$.ajax({
    url,
    method: 'POST',
    success: () => {
      dispatch({ type: CHECK_IN_SUCCESS })
    },
    error: (xhr, status, err) => {
      console.error(url, status, err.toString())
      dispatch({
        type: CHECK_IN_FAILURE,
        payload: err.toString(),
      })
    },
  })
}

export const removeCheckIn = () => (dispatch, getState) => {
  const state = getState()
  const { ticket, removeCheckInUrl } = state.ticketsScanner.ticket

  if (!ticket || !ticket.id) return

  const reason = window.prompt('Why are you removing this check-in?', '')

  let url = removeCheckInUrl.replace(/:id/, ticket.id)
  if (reason !== null && reason !== '') {
    url += '?reason=' + encodeURI(reason)
  }

  dispatch({ type: REMOVE_CHECK_IN_REQUEST })

  window.$.ajax({
    url,
    method: 'POST',
    success: () => {
      dispatch({ type: REMOVE_CHECK_IN_SUCCESS })
    },
    error: (xhr, status, err) => {
      console.error(url, status, err.toString())
      dispatch({
        type: REMOVE_CHECK_IN_FAILURE,
        payload: err.toString(),
      })
    },
  })
}

export const updateQueryForTicket = (query) => (dispatch) => {
  if (isBarcodeString(query)) {
    dispatch(findTicket(query))
  }
}

// A forced search is the same operation for a ticket: the barcode either matches or it does
// not, so there is nothing extra to force. Kept as a separate export so the scanner's two call
// sites stay readable.
export const forceSearchTicket = updateQueryForTicket

// Selectors
export const selectTicket = (state) => state.ticketsScanner?.ticket?.ticket
export const selectTicketLoading = (state) => state.ticketsScanner?.ticket?.loading || false
export const selectTicketError = (state) => state.ticketsScanner?.ticket?.error
